/**
 * Telemetria mínima do beta — o lado do aparelho (rodada 2B2).
 *
 * Responde "quem abriu o app e que áreas abriu" e nada mais. As ações reais
 * já estão nas tabelas do produto; aqui só entram `app_opened` e
 * `feature_opened`, com o contrato fechado de `@nv/core/telemetry`
 * (validado antes de entrar na fila e de novo no servidor).
 *
 * Decisões:
 *   - **abertura, não render**: `app_opened` no primeiro carregamento
 *     autenticado, e de novo só depois de ≥ 30 min em segundo plano. A última
 *     abertura fica em storage, por conta: um reload logo depois não conta;
 *   - **uso, não render**: `feature_opened` quando uma área é aberta, com
 *     anti-ruído local — mesma pessoa + mesma área + mesma viagem em 5 min é
 *     um evento só. A janela fica em storage, por conta: um reload não
 *     transforma a mesma tela em "popularidade";
 *   - **fila própria, por conta, best-effort**: ≤ 100 itens, 7 dias, chave
 *     `nv:session:…` (o logout varre) e cada item leva o `userId` de quem o
 *     gerou — outra conta nunca envia o que não é dela. Não usa o outbox de
 *     negócio: telemetria perdida é aceitável, ação perdida não;
 *   - **nunca lança**: storage quebrado, rede fora, servidor recusando —
 *     tudo vira no-op. O transporte marca o pedido para o fetch instrumentado
 *     ignorar: falha de telemetria não vira diagnóstico.
 *   - **só com identidade**: sem `userId` não há evento nenhum.
 */
import { LIMITES_TELEMETRIA, PRODUCT_FEATURES, validarEventoDeProduto, type ProductEvent, type ProductFeature } from '@nv/core';

export const CHAVE_FILA = 'nv:session:telemetria';
export const CHAVE_ABERTURA = 'nv:session:telemetria:abertura';
export const CHAVE_RECENTES = 'nv:session:telemetria:recentes';

export type Transporte = (lote: ProductEvent[]) => Promise<{ status: number }>;

export interface TelemetriaDeps {
  usuarioAtual: () => string | null;
  transporte: Transporte;
  clientRelease: string;
  agora?: () => number;
  online?: () => boolean;
  standalone?: () => boolean;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  eventId?: () => string;
  /** Agenda um envio (padrão `setTimeout`); injetável para o teste ser determinístico. */
  agendar?: (fn: () => void, ms: number) => void;
}

interface ItemDaFila {
  userId: string;
  evento: ProductEvent;
  em: number;
}

const ENVIO_DEBOUNCE_MS = 5_000;

export class Telemetria {
  private escopo: string | null | undefined = undefined;
  private ocultoDesde: number | null = null;
  private enviando = false;
  private envioAgendado = false;

  constructor(private readonly deps: TelemetriaDeps) {}

  /* ───────────── eventos ───────────── */

  /** Abertura do app: primeira vez autenticado, ou depois de ≥ 30 min em segundo plano. */
  appAberto(tripId?: string): boolean {
    const userId = this.sincronizarEscopo();
    if (!userId) return false;
    const agora = this.agora();
    const ultima = this.lerUltimaAbertura(userId);
    if (ultima !== null && agora - ultima < LIMITES_TELEMETRIA.appOpenedIntervaloMs) return false;
    if (!this.enfileirar(userId, { eventName: 'app_opened', tripId })) return false;
    this.gravarUltimaAbertura(userId, agora);
    return true;
  }

  aoFicarOculto(): void {
    this.ocultoDesde = this.agora();
  }

  /** Voltou do segundo plano: conta como abertura se ficou fora tempo suficiente. */
  aoFicarVisivel(tripId?: string): boolean {
    const oculto = this.ocultoDesde;
    this.ocultoDesde = null;
    if (oculto === null || this.agora() - oculto < LIMITES_TELEMETRIA.appOpenedIntervaloMs) return false;
    return this.appAberto(tripId);
  }

  /** Uma área foi aberta. Anti-ruído: mesma área + mesma viagem em 5 min não repete. */
  featureAberta(feature: ProductFeature, tripId?: string): boolean {
    const userId = this.sincronizarEscopo();
    if (!userId) return false;
    if (!(PRODUCT_FEATURES as readonly string[]).includes(feature)) return false;
    const chave = `${feature}|${tripId ?? ''}`;
    const agora = this.agora();
    const recentes = this.lerRecentes(userId);
    const ultimo = recentes[chave];
    if (ultimo !== undefined && agora - ultimo < LIMITES_TELEMETRIA.featureDedupeMs) return false;
    if (!this.enfileirar(userId, { eventName: 'feature_opened', feature, tripId })) return false;
    recentes[chave] = agora;
    this.gravarRecentes(userId, recentes);
    return true;
  }

  /* ───────────── fila ───────────── */

  /** Eventos da conta atual ainda não enviados (vivos). */
  pendentes(): ProductEvent[] {
    const userId = this.sincronizarEscopo();
    return this.lerFila()
      .filter((i) => i.userId === userId)
      .map((i) => i.evento);
  }

  /** Envia o que houver da conta atual, em lotes. Uma passada; nunca lança. */
  async enviarPendentes(): Promise<void> {
    if (this.enviando) return;
    this.enviando = true;
    try {
      const userId = this.sincronizarEscopo();
      if (!userId || !this.online()) return;
      const fila = this.lerFila();
      const meus = fila.filter((i) => i.userId === userId);
      if (meus.length === 0) return;
      const manter = new Set<string>();
      for (let i = 0; i < meus.length; i += LIMITES_TELEMETRIA.loteMax) {
        const lote = meus.slice(i, i + LIMITES_TELEMETRIA.loteMax);
        let status: number | null = null;
        try {
          status = (await this.deps.transporte(lote.map((x) => x.evento))).status;
        } catch {
          status = null; // rede: fica para a próxima
        }
        if (status === null || status >= 500) for (const x of lote) manter.add(x.evento.eventId);
        // 2xx: enviado. 4xx: recusado, não insiste (o contrato é o mesmo dos dois lados).
      }
      this.gravarFila(this.lerFila().filter((i) => i.userId !== userId || manter.has(i.evento.eventId)));
    } catch {
      /* best-effort */
    } finally {
      this.enviando = false;
    }
  }

  /* ───────────── internos ───────────── */

  private sincronizarEscopo(): string | null {
    const atual = this.deps.usuarioAtual();
    if (this.escopo !== undefined && this.escopo !== atual) {
      this.ocultoDesde = null;
    }
    this.escopo = atual;
    return atual;
  }

  private enfileirar(userId: string, base: { eventName: ProductEvent['eventName']; feature?: ProductFeature; tripId?: string }): boolean {
    const agora = this.agora();
    const bruto: Record<string, unknown> = {
      eventId: this.deps.eventId?.() ?? gerarUuid(),
      eventName: base.eventName,
      occurredAt: new Date(agora).toISOString(),
      clientRelease: this.deps.clientRelease,
      online: this.online(),
      standalone: this.deps.standalone?.() ?? false,
    };
    if (base.feature) bruto['feature'] = base.feature;
    if (base.tripId) bruto['tripId'] = base.tripId;
    // O mesmo contrato do servidor, antes de gravar qualquer coisa.
    const validado = validarEventoDeProduto(bruto);
    if (!validado.ok) return false;
    const fila = this.lerFila();
    fila.push({ userId, evento: validado.evento, em: agora });
    while (fila.length > LIMITES_TELEMETRIA.filaMax) fila.shift();
    this.gravarFila(fila);
    this.agendarEnvio();
    return true;
  }

  private agendarEnvio(): void {
    if (this.envioAgendado) return;
    this.envioAgendado = true;
    const agendar = this.deps.agendar ?? ((fn, ms) => void setTimeout(fn, ms));
    agendar(() => {
      this.envioAgendado = false;
      void this.enviarPendentes();
    }, ENVIO_DEBOUNCE_MS);
  }

  private lerFila(): ItemDaFila[] {
    const st = this.storage();
    if (!st) return [];
    try {
      const bruto = st.getItem(CHAVE_FILA);
      if (!bruto) return [];
      const lista = JSON.parse(bruto) as unknown;
      if (!Array.isArray(lista)) return [];
      const limite = this.agora() - LIMITES_TELEMETRIA.filaTtlMs;
      return lista.filter((i): i is ItemDaFila => !!i && typeof i === 'object' && typeof (i as ItemDaFila).userId === 'string' && typeof (i as ItemDaFila).em === 'number' && (i as ItemDaFila).em >= limite && validarEventoDeProduto((i as ItemDaFila).evento).ok);
    } catch {
      return [];
    }
  }

  private gravarFila(fila: ItemDaFila[]): void {
    const st = this.storage();
    if (!st) return;
    try {
      if (fila.length === 0) st.removeItem(CHAVE_FILA);
      else st.setItem(CHAVE_FILA, JSON.stringify(fila));
    } catch {
      /* sem espaço: perde-se telemetria, nunca a ação da pessoa */
    }
  }

  private lerUltimaAbertura(userId: string): number | null {
    const st = this.storage();
    if (!st) return null;
    try {
      const bruto = st.getItem(CHAVE_ABERTURA);
      if (!bruto) return null;
      const v = JSON.parse(bruto) as { userId?: unknown; em?: unknown };
      return v.userId === userId && typeof v.em === 'number' ? v.em : null;
    } catch {
      return null;
    }
  }

  private gravarUltimaAbertura(userId: string, em: number): void {
    const st = this.storage();
    if (!st) return;
    try {
      st.setItem(CHAVE_ABERTURA, JSON.stringify({ userId, em }));
    } catch {
      /* idem */
    }
  }

  /** Janela de anti-ruído da conta atual (área|viagem → instante), sem entradas vencidas. */
  private lerRecentes(userId: string): Record<string, number> {
    const st = this.storage();
    if (!st) return {};
    try {
      const bruto = st.getItem(CHAVE_RECENTES);
      if (!bruto) return {};
      const v = JSON.parse(bruto) as { userId?: unknown; itens?: unknown };
      if (v.userId !== userId || !v.itens || typeof v.itens !== 'object') return {};
      const limite = this.agora() - LIMITES_TELEMETRIA.featureDedupeMs;
      const vivos: Record<string, number> = {};
      for (const [k, em] of Object.entries(v.itens as Record<string, unknown>)) if (typeof em === 'number' && em >= limite) vivos[k] = em;
      return vivos;
    } catch {
      return {};
    }
  }

  private gravarRecentes(userId: string, itens: Record<string, number>): void {
    const st = this.storage();
    if (!st) return;
    try {
      st.setItem(CHAVE_RECENTES, JSON.stringify({ userId, itens }));
    } catch {
      /* idem */
    }
  }

  private storage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
    if (this.deps.storage !== undefined) return this.deps.storage;
    try {
      return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
      return null;
    }
  }

  private agora(): number {
    return this.deps.agora?.() ?? Date.now();
  }

  private online(): boolean {
    return this.deps.online?.() ?? (typeof navigator === 'undefined' ? true : navigator.onLine);
  }
}

function gerarUuid(): string {
  return globalThis.crypto.randomUUID();
}

/** Transporte padrão: a rota first-party, marcada para o fetch instrumentado ignorar. */
export const transporteHttp: Transporte = async (lote) => {
  const res = await fetch('/api/v1/me/product-events', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', 'x-nv-diagnostico': '1' },
    body: JSON.stringify(lote),
    keepalive: true,
  });
  return { status: res.status };
};
